import Phaser from "phaser";
import {KitchenScene, logger} from "./scene";
import {Cook, CookSpriteNames} from "../model/cooks";
import {animationKeyOf} from "./animations";
import {IngredientSprite} from "./ingredient_sprite";
import {IGameSprite} from "./game_sprite_base";
import {GameSchema} from "../model/game_element_schema";
import {convertDirectionToNormalizedXY} from "../model/direction";
import Sprite = Phaser.GameObjects.Sprite;
import Container = Phaser.GameObjects.Container;

class UndefinedIngredientError extends Error {
    constructor(message: string) {
        super(message)
    }
}

// Lerp factor for smooth position interpolation toward server state.
// All cooks (local and remote) use the same value - no client-side prediction.
const LERP_FACTOR = 0.3;

export class CookSprite extends Container implements IGameSprite<Cook> {

    instance: Cook
    characterSprite: Sprite
    carrying: IngredientSprite
    isPlaying: boolean
    isLocal: boolean
    message: string;
    name: string;

    // Server-authoritative target position for interpolation
    private serverTargetX: number
    private serverTargetY: number

    constructor(scene: KitchenScene, cook: Cook) {
        super(scene, cook.x, cook.y);
        this.instance = cook
        this.isLocal = (this.scene as KitchenScene).sessionId == cook.id
        this.serverTargetX = cook.x
        this.serverTargetY = cook.y
        scene.children.add(this)
        this.characterSprite = scene.add.sprite(0, 0, cook.skinName).setScale(2)
        this.characterSprite.setOrigin(0.5, 0.5)
        this.add(this.characterSprite)
        this.setScale(1)
        // 44px hit area for touch, centered at container origin (0,0)
        const HIT_SIZE = 44;
        this.setSize(HIT_SIZE, HIT_SIZE);
        this.setInteractive(
            new Phaser.Geom.Rectangle(-HIT_SIZE / 2, -HIT_SIZE / 2, HIT_SIZE, HIT_SIZE),
            Phaser.Geom.Rectangle.Contains
        );
    }

    updateDirection(sprite_key: CookSpriteNames) {
        this.updateAnimationDirection();
        if (this.carrying != undefined) {
            this.updateCarryingDirection();
        }
    }

    selectAnimation() {
        let direction = this.instance.direction
        let key = animationKeyOf(this.instance.skinName, direction);
        return key;
    }

    carry(ingredientSprite: IngredientSprite) {
        if (!ingredientSprite) {
            throw new UndefinedIngredientError('cannot use undefined as an ingredient')
        }
        if (this.carrying == ingredientSprite) {
            return false
        }
        this.add(ingredientSprite)
        this.carrying = ingredientSprite
        ingredientSprite.bePicked(this)
        ingredientSprite.setScale(ingredientSprite.scaleX * 0.75, ingredientSprite.scaleY * 0.75)
        this.updateCarryingDirection();
        return true
    }

    drop(): IngredientSprite | false {
        if (!this.carrying)
            return false
        let ingredientSprite = this.carrying
        this.remove(ingredientSprite, false) // risk of double destroy
        ingredientSprite.beDropped()
        ingredientSprite.setScale(ingredientSprite.scaleX * 4 / 3, ingredientSprite.scaleY * 4 / 3)
        this.carrying = undefined
        return ingredientSprite
    }

    destroy(fromScene?: boolean) {
        super.destroy(fromScene);
    }

    getHeight = (): number => this.characterSprite.displayHeight

    getWidth = (): number => this.characterSprite.displayWidth

    bindToInstance() {
        this.bindToServerPosition()
        // All cooks update animation from server velocity changes
        this.instance.listen("velocityX", () => {
            this.updateDirection(this.instance.skinName)
        })
        this.instance.listen("velocityY", () => {
            this.updateDirection(this.instance.skinName)
        })
    }

    bindToServerPosition() {
        this.instance.listen("x", value => {
            this.serverTargetX = value
            logChange(this, "x", value)
        })
        this.instance.listen("y", value => {
            this.serverTargetY = value
            logChange(this, "y", value)
        })
    }

    // Called each frame from scene.update() for all cooks
    interpolateToServerPosition() {
        const dx = this.serverTargetX - this.x;
        const dy = this.serverTargetY - this.y;
        this.setPosition(
            this.x + dx * LERP_FACTOR,
            this.y + dy * LERP_FACTOR
        );
    }

    snapToServerPosition() {
        this.setPosition(this.serverTargetX, this.serverTargetY)
    }

    private updateCarryingDirection() {
        let shift = convertDirectionToNormalizedXY(this.instance.direction)

        this.carrying.x = shift.x * this.getWidth() * 0.5
        this.carrying.y = shift.y * this.getHeight() * 0.5
    }

    private updateAnimationDirection() {
        let key = this.selectAnimation();
        this.characterSprite.anims.play(key, true);
        this.isPlaying = !(this.instance.velocityX == 0 && this.instance.velocityY == 0)
        this.characterSprite.anims.isPlaying = this.isPlaying
    }
}

export function logChange(sprite: IGameSprite<GameSchema>, field: string, value: number, note: string = undefined) {
    logger.debug(`updated ${sprite.constructor.name} ${sprite.instance.printableId}.${field}=${value}${note != undefined || note == '' ? ' (' + note + ')' : ''}`)
}
